<template>
  <div>

    <table-tools :actions="[{
      title: $t('actions.save'),
      icon: '$confirm',
      click: submit,
      busy: submitting,
      hidden: false,
    }]">

      <table-filter>
        <o-text-field
          :label="$t('titles.id')"
          v-model.number="filterFormState.id"
          clearable
        />
      </table-filter>

      <table-filter>
        <o-text-field
          :label="$t('titles.username')"
          v-model="filterFormState.username"
          clearable
        />
      </table-filter>

      <table-filter>
        <o-text-field
          :label="$t('titles.name')"
          v-model="filterFormState.name"
          clearable
        />
      </table-filter>

      <table-filter>
        <o-select
          :label="$t('titles.role')"
          v-model="filterFormState.role"
          :items="[{value: 'user', title: 'User'}, {value: 'admin', title: 'Admin'}, {value: 'super_admin', title: 'Super Admin'}]"
          clearable
        />
      </table-filter>

    </table-tools>

    <v-data-table
      :items="filteredFormRows"
      :headers="headers"
      :loading="loading"
    >

      <template v-slot:item.user.name="{ item }">
        {{ item.user.firstName }} {{ item.user.lastName }}
      </template>

      <template v-slot:item.user.role="{ item }">
        {{ $t(`enums.user_roles.${item.user.role}`) }}
      </template>

      <template v-slot:item.user.live="{ item }">
        {{ $t(item.user.live ? 'misc.live' : 'misc.onboarded') }}
      </template>

      <template v-slot:item.universalInventory="{ item }">
        <user-solution-control
          v-model="item.universalInventory"
          :editable="item.user.role === 'user'"
          :busy="submitting"
          class="my-2"
        />
      </template>

      <template v-slot:item.rateManagement="{ item }">
        <user-solution-control
          v-model="item.rateManagement"
          :editable="item.user.role === 'user'"
          :busy="submitting"
          class="my-2"
        />
      </template>

      <template v-slot:item.blackouts="{ item }">
        <user-solution-control
          v-model="item.blackouts"
          :editable="item.user.role === 'user'"
          :busy="submitting"
          class="my-2"
        />
      </template>

      <template v-slot:item.reporting="{ item }">
        <user-solution-control
          v-model="item.reporting"
          :editable="item.user.role === 'user'"
          :busy="submitting"
          class="my-2"
        />
      </template>

      <template v-slot:item.uri="{ item }">
        <user-solution-control
          :hide-read="true"
          v-model="item.uri"
          :editable="['admin', 'user'].includes(item.user.role)"
          :busy="submitting"
          class="my-2"
        />

        <v-tooltip text="URI also requires write access to Rate Management and read access to Reporting" location="bottom right">
          <template v-slot:activator="{ props }">
            <v-icon icon="$warning" v-bind="props" color="error" class="ms-2" :style="{visibility: (item.uri === UserSolutionControlValue.Write && (item.rateManagement !== UserSolutionControlValue.Write || item.reporting === UserSolutionControlValue.None)) ? 'visible' : 'hidden'}" />
          </template>
        </v-tooltip>

      </template>

      <template v-slot:item.terminal="{ item }">
        <user-solution-control
          :hide-read="true"
          v-model="item.terminal"
          :editable="item.user.role === 'super_admin'"
          :busy="submitting"
          class="my-2"
        />
      </template>

    </v-data-table>

  </div>
</template>

<script setup lang="ts">

import { computed, onMounted, reactive, ref } from 'vue'
import { useTitle } from '@vueuse/core'

// components
import UserSolutionControl from '../../UserSolutionControl.vue'

// models
import Location from '../../../models/Location'
import User from '../../../models/User'
import UserSolution, {
  type UpdateUserSolutionByLocationRequest,
  type UpdateUserSolutionByLocationRequestItem
} from '../../../models/UserSolution'

// misc
import { useI18n } from 'vue-i18n'
import { auth } from '../../../store/auth.module'
import NotificationService from '../../../services/NotificationService'
import { UserSolutionControlValue } from './types'
import PermissionsService from '../../../services/PermissionsService'

const props = defineProps({

  location: {
    type: Location,
    required: true,
  },

})

const canSeeIds = PermissionsService.can('seeids')
// @ts-ignore
const isSuperAdmin = auth.state.user.role === 'super_admin'
const canManageUri = isSuperAdmin
const canManageTerminal = isSuperAdmin

const { t } = useI18n()

const title = useTitle()
title.value = t('titles.location_users', {location_name: props.location.locationName})

/**
 * Initialization
 */

const loading = ref(false)

onMounted(() => {

  loading.value = true

  UserSolution.fetchByLocationID(props.location.ID)
    .catch((e) => {
      console.error(e)
      NotificationService.error("Couldn't load users!")
    })
    .then(() => {
      populateFormRows()
    })
    .finally(() => {
      loading.value = false
    })

})

function populateFormRows() {

  const users = User.query()
    .where('organizationID', props.location.organizationID)
    .orWhere('role', 'super_admin')
    .whereHas('userSolutions', (query) => {
      query.where('locationID', props.location.ID)
    })
    .with('userSolutions', (query) => {
      query.where('locationID', props.location.ID)
    })
    .orderBy('username')
    .get()

  formRows.value.length = 0

  users.forEach((user) => {

    const us = user.userSolutions[0]

    const formRow: UserSolutionFormRow = {
      user: user,
      universalInventory: boolsToFormValue(us.universalInventoryRead, us.universalInventoryWrite),
      rateManagement: boolsToFormValue(us.rateManagementRead, us.rateManagementWrite),
      blackouts: boolsToFormValue(us.blackoutsRead, us.blackoutsWrite),
      reporting: boolsToFormValue(us.reportingRead, us.reportingWrite),
      uri: us.uriWrite ? UserSolutionControlValue.Write : UserSolutionControlValue.None,
      terminal: us.terminalWrite ? UserSolutionControlValue.Write : UserSolutionControlValue.None,
    }

    formRows.value.push(formRow)

  })

}

/**
 * Table headers
 */

const headers = [
  {key: 'user.username', title: t('titles.username')},
  {key: 'user.name', title: t('titles.name')},
  {key: 'user.role', title: t('titles.role')},
  {key: 'user.live', title: t('titles.status')},
  {key: 'universalInventory', title: t('enums.solutions.universal_inventory')},
  {key: 'rateManagement', title: t('enums.solutions.rate_management')},
  {key: 'blackouts', title: t('enums.solutions.blackouts')},
  {key: 'reporting', title: t('enums.solutions.reporting')},
]

if (canSeeIds) {
  headers.unshift({key: 'user.ID', title: t('titles.id')})
}

if (canManageUri) {
  headers.push({key: 'uri', title: t('enums.solutions.uri')})
}

if (canManageTerminal) {
  headers.push({key: 'terminal', title: t('enums.solutions.terminal')})
}

/**
 * Main form
 */

interface UserSolutionFormRow {
  user: User
  universalInventory: UserSolutionControlValue,
  rateManagement: UserSolutionControlValue,
  blackouts: UserSolutionControlValue,
  reporting: UserSolutionControlValue,
  uri: UserSolutionControlValue,
  terminal: UserSolutionControlValue,
}

const formRows = ref<UserSolutionFormRow[]>([])

/**
 * Filter form
 */

interface FilterFormState {
  id: null | number
  username: null | string
  name: null | string
  role: null | string
}

const filterFormState = reactive<FilterFormState>({
  id: null,
  username: null,
  name: null,
  role: null,
})

const filteredFormRows = computed(() => {

  return formRows.value.filter((formRow: UserSolutionFormRow) => {

    if (filterFormState.id && formRow.user.ID !== filterFormState.id) {
      return false
    }

    if (filterFormState.role && formRow.user.role !== filterFormState.role) {
      return false
    }

    if (filterFormState.username && !formRow.user.username.toLowerCase().includes(filterFormState.username.toLowerCase()) ) {
      return false
    }

    if (filterFormState.name) {
      const nameFilter = filterFormState.name.toLowerCase()
      if (
        !formRow.user.firstName.toLowerCase().includes(nameFilter) &&
        !formRow.user.lastName.toLowerCase().includes(nameFilter)
      ) {
        return false
      }
    }

    return true    

  })

})

/**
 * Submission
 */

const submitting = ref(false)

function submit() {

  submitting.value = true

  const payload: UpdateUserSolutionByLocationRequest = {userSolutions: []}

  formRows.value.forEach((formRow) => {

    // Determine whether this user's form values differ from its original
    // values. If so, queue it for update.

    const us = formRow.user.userSolutions[0]

    const payloadItem: UpdateUserSolutionByLocationRequestItem = {
      userID: formRow.user.ID,
      universalInventoryRead: us.universalInventoryRead,
      universalInventoryWrite: us.universalInventoryWrite,
      rateManagementRead: us.rateManagementRead,
      rateManagementWrite: us.rateManagementWrite,
      blackoutsRead: us.blackoutsRead,
      blackoutsWrite: us.blackoutsWrite,
      reportingRead: us.reportingRead,
      reportingWrite: us.reportingWrite,
      rateParityRead: us.rateParityRead,
      rateParityWrite: us.rateParityWrite,
      maxCapacityRead: us.maxCapacityRead,
      maxCapacityWrite: us.maxCapacityWrite,
    }

    const solutions = ['universalInventory', 'rateManagement', 'blackouts', 'reporting']

    const perms = [
      'universalInventoryRead', 'universalInventoryWrite',
      'rateManagementRead', 'rateManagementWrite',
      'blackoutsRead', 'blackoutsWrite',
      'reportingRead', 'reportingWrite',
    ]

    if (canManageUri) {
      payloadItem.uriRead = us.uriRead
      payloadItem.uriWrite = us.uriWrite
      solutions.push('uri')
      perms.push('uriRead')
      perms.push('uriWrite')
    }

    if (canManageTerminal) {
      payloadItem.terminalRead = us.terminalRead
      payloadItem.terminalWrite = us.terminalWrite
      solutions.push('terminal')
      perms.push('terminalRead')
      perms.push('terminalWrite')
    }

    let dirty = false

    solutions.forEach((solution) => {

      // @ts-ignore
      const read = formRow[solution] === UserSolutionControlValue.Read || formRow[solution] === UserSolutionControlValue.Write
      // @ts-ignore
      const write = formRow[solution] === UserSolutionControlValue.Write

      const readKey = `${solution}Read`
      const writeKey = `${solution}Write`

      // @ts-ignore
      if (payloadItem[readKey] !== read) {
        // @ts-ignore
        payloadItem[readKey] = read
        dirty = true
      }

      // @ts-ignore
      if (payloadItem[writeKey] !== write) {
        // @ts-ignore
        payloadItem[writeKey] = write
        dirty = true
      }

    })

    if (dirty) {
      payload.userSolutions.push(payloadItem)
    }

  })

  if (!payload.userSolutions.length) {
    NotificationService.success('Nothing to save')
    submitting.value = false
    return
  }

  UserSolution.updateByLocationID(props.location.ID, payload)
    .then(() => {
      NotificationService.success('User solutions updated')
      populateFormRows()
    })
    .catch((e) => {
      console.error(e)
      NotificationService.error('Couldn\'t update user solutions!')
    })
    .finally(() => {
      submitting.value = false
    })

}

/**
 * Misc
 */

function boolsToFormValue(read: boolean, write: boolean): UserSolutionControlValue {

  if (write) {
    return UserSolutionControlValue.Write
  }

  return read ? UserSolutionControlValue.Read : UserSolutionControlValue.None

}

</script>
